/**
 * REPORT-UPLOAD-SUPERVISOR.WIDGET
 * Dropzone for supervisors to upload reports and assign them to users
 */

import { useEffect, useState } from "react"
import { WithTranslation, withTranslation } from "react-i18next"
import { store } from "@/index"
import { reportActivate } from "@/redux/report.actions"
import { Report, ReportState } from "@/redux/report.types"
import { faFile, faTrash, faUser } from "@fortawesome/free-solid-svg-icons"
import { connect } from "react-redux"
import Chip from "@/components/chip"
import SearchUser from "@/components/search-user"
import UploadButtonWidget from "@/widgets/upload-button.widget"
import ListItem from "@/components/list-item"
import ListIcon from "@/components/list-icon"
import ListButton from "@/components/list-button"
import ListContainer from "@/components/list-container"
import ListCell from "@/components/list-cell"
import { User } from "@/redux/user.types"

interface StateProps {
  report: ReportState
}

interface OwnProps {
  droppedFile: File | null
  selectedUser: User | null
  onDeleteRequest: () => void
  onFileDrop: (files: File[]) => void
  onSelectUser: (user: User) => void
}

type Props = StateProps & OwnProps & WithTranslation

function ReportUploadSupervisorWidget(props: Props) {
  const { t } = props
  const [showSearch, setShowSearch] = useState(false)

  // Auto-open search when a file is dropped
  useEffect(() => {
    if (props.droppedFile) setShowSearch(true)
  }, [props.droppedFile])

  const uploadedReports = props.report.list.filter((x) => x.options.downloadLink)

  return (
    <div className="p-4 flex flex-col gap-4">
      <UploadButtonWidget
        onUpload={props.onFileDrop}
        acceptedFiles=".pdf,.pptx,.docx"
        maxSize={50000000}
        showFrameStyle
      />

      {props.droppedFile && (
        <div className="relative">
          {props.selectedUser && !showSearch ? (
            <div className="inline-flex">
              <Chip icon={faUser} onDelete={() => setShowSearch(true)}>
                {props.selectedUser.username}
              </Chip>
            </div>
          ) : showSearch ? (
            <SearchUser
              onSelect={(user: User) => {
                props.onSelectUser(user)
                setShowSearch(false)
              }}
              onClose={() => setShowSearch(false)}
            />
          ) : (
            <div className="text-sm grey-t">{t("report_upload_select_user")}</div>
          )}
        </div>
      )}

      {uploadedReports.length > 0 && (
        <div>
          <h4 className="m-0 grey-t">{t("report_upload_list")}</h4>
          <ListContainer>
            {uploadedReports.map((x: Report) => (
              <ListItem key={x.id} isEditable isSmall>
                <ListIcon fallbackIcon={faFile} />
                <ListCell width={300} text={x.name} />
                <ListCell width={200} text={x.options.username || ""} />
                <div className="flex-1" />
                <ListButton
                  icon={faTrash}
                  text={t("utils_delete")}
                  onClick={() => {
                    store.dispatch(reportActivate(x))
                    props.onDeleteRequest()
                  }}
                />
              </ListItem>
            ))}
          </ListContainer>
        </div>
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({
  report: state.report
})

export default connect(mapStateToProps)(withTranslation()(ReportUploadSupervisorWidget))
